(() => {
  const CONTEXT_ID = 'TanLeadContext';
  const STORAGE_KEY = 'tan.lead.context.v1';
  const PENDING_KEY = 'tan.quote.pending.v1';
  const REPORTED_KEY = 'tan.quote.reported.v1';
  const EVENT_PREFIX = 'the_art_neon:';
  const QUOTE_PATHS = ['/pages/custom-neon-sign-quote', '/pages/upload-your-own-design-or-logo', '/pages/bulk-orders-trade'];
  const EVENT_VERSION = '2.0';

  const HUBS = {
    'outdoor-neon-signs': { id: 'outdoor', labelKey: 'outdoor_neon_signs' },
    'restaurant-cafe': { id: 'restaurant_cafe', labelKey: 'restaurant_cafe' },
    'salon-barber': { id: 'salon_barber', labelKey: 'salon_barber' },
    'event-activations': { id: 'events_activations', labelKey: 'event_activations' },
    'retail-storefront': { id: 'retail_storefront', labelKey: 'retail_storefront' },
    'office-reception': { id: 'office_reception', labelKey: 'office_reception' },
    'hotel-hospitality': { id: 'hotels_hospitality', labelKey: 'hotel_hospitality' },
    'gym-fitness': { id: 'gyms_fitness', labelKey: 'gym_fitness' },
    'real-estate-property': { id: 'real_estate_property', labelKey: 'real_estate_property' },
    'tattoo-piercing': { id: 'tattoo_piercing', labelKey: 'tattoo_piercing' },
    'creator-studios': { id: 'creator_studios', labelKey: 'creator_studios' },
    'automotive-studios': { id: 'automotive', labelKey: 'automotive_studios' },
    'weddings-celebrations': { id: 'weddings_celebrations', labelKey: 'weddings_celebrations' },
    businesshub: { id: 'business_hub', labelKey: 'business_hub' },
    b2b: { id: 'quote_form', labelKey: 'business_quotation' },
    'upload-quote': { id: 'quote_form', labelKey: 'custom_design_quotation' },
    trade: { id: 'trade_procurement', labelKey: 'bulk_trade' },
  };
  const HUB_IDS = new Set(Object.values(HUBS).map((hub) => hub.id));
  const FORM_HUB_IDS = new Set(['quote_form', 'trade_procurement']);
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'dclid', 'fbclid', 'msclkid', 'ttclid'];
  const QUOTE_EVENT_NAMES = new Set([
    'quote_start',
    'quote_step_view',
    'quote_step_complete',
    'quote_restore',
    'quote_restart',
    'quote_upload_added',
    'generate_lead',
  ]);
  const emittedOnce = new Set();
  const publishQueue = [];
  const briefStates = new WeakMap();
  const designerStarts = new WeakSet();
  const designerNexts = new WeakSet();
  let publishTimer = 0;

  const clean = (value, limit = 100) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit);
  const cleanMultiline = (value, limit = 6000) => String(value || '')
    .replace(/\r\n?/g, '\n')
    .replace(/[\t ]+\n/g, '\n')
    .trim()
    .slice(0, limit);

  const readJson = (id) => {
    try {
      return JSON.parse(document.getElementById(id)?.textContent || '{}');
    } catch (_) {
      return {};
    }
  };
  const liquidContext = readJson(CONTEXT_ID);
  const leadI18n = liquidContext.i18n && typeof liquidContext.i18n === 'object'
    ? liquidContext.i18n
    : {};
  Object.values(HUBS).forEach((hub) => {
    hub.label = clean(leadI18n.hubs?.[hub.labelKey], 100);
  });
  const localizedHubLabel = (hubId) => clean(
    Object.values(HUBS).find((hub) => hub.id === hubId)?.label,
    100
  );
  const templateSuffix = clean(liquidContext.template_suffix, 60);
  const currentHub = HUBS[templateSuffix] || null;
  const contentHub = FORM_HUB_IDS.has(currentHub?.id) ? null : currentHub;
  const designMode = liquidContext.design_mode === true;

  const storage = {
    get(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (_) {
        // The form remains usable when storage is unavailable.
      }
    },
  };

  const query = new URLSearchParams(window.location.search);
  const safePath = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return clean(url.pathname || '/', 300);
    } catch (_) {
      return clean(window.location.pathname || '/', 300);
    }
  };
  const safeReferrer = () => {
    if (!document.referrer) return '';
    try {
      const url = new URL(document.referrer);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      return clean(`${url.hostname}${url.pathname}`, 300);
    } catch (_) {
      return '';
    }
  };
  const marketingStorageAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.marketingAllowed === 'function') {
        return privacy.marketingAllowed() === true;
      }
      const consent = privacy?.currentVisitorConsent?.();
      if (consent && Object.prototype.hasOwnProperty.call(consent, 'marketing')) {
        return consent.marketing === true || consent.marketing === 'yes';
      }
    } catch (_) {
      // Unknown consent never grants marketing identifier storage.
    }
    return false;
  };
  const captureAttribution = () => {
    const existing = storage.get(STORAGE_KEY) || {};
    const next = { ...existing };
    const currentPath = safePath(window.location.href);
    if (!next.landing_path) next.landing_path = currentPath;
    if (!next.landing_referrer) next.landing_referrer = safeReferrer();

    let capturedCampaign = false;
    UTM_KEYS.forEach((key) => {
      const value = clean(query.get(key), 150);
      if (!value) return;
      if (!next[`first_${key}`]) next[`first_${key}`] = value;
      next[key] = value;
      capturedCampaign = true;
    });

    if (marketingStorageAllowed()) {
      CLICK_ID_KEYS.forEach((key) => {
        const value = clean(query.get(key), 300);
        if (!value) return;
        if (!next[`first_${key}`]) next[`first_${key}`] = value;
        next[key] = value;
        capturedCampaign = true;
      });
    } else {
      CLICK_ID_KEYS.forEach((key) => {
        delete next[key];
        delete next[`first_${key}`];
      });
    }

    if (capturedCampaign) {
      next.attribution_path = currentPath;
      next.attribution_updated_at = Date.now();
    }
    storage.set(STORAGE_KEY, next);
    return next;
  };
  let storedContext = captureAttribution();
  const currentLeadContext = () => ({
    ...storedContext,
    ...(storage.get(STORAGE_KEY) || {}),
  });
  const queryHub = clean(query.get('tan_hub'), 60);
  const sourceHubId = HUB_IDS.has(queryHub)
    ? queryHub
    : (contentHub?.id || (HUB_IDS.has(storedContext.hub) ? storedContext.hub : ''));
  const sourceHubLabel = contentHub?.label
    || localizedHubLabel(sourceHubId)
    || clean(storedContext.hub_label, 100);

  const commonPayload = () => {
    const attribution = currentLeadContext();
    return {
      event_version: EVENT_VERSION,
      hub: sourceHubId || 'unclassified',
      hub_template: templateSuffix || 'default',
      source_path: clean(contentHub ? window.location.pathname : (attribution.source_path || window.location.pathname), 100),
      source_cta: clean(query.get('tan_cta') || attribution.source_cta || 'direct', 100),
      country_code: clean(liquidContext.country_code, 10),
      language_code: clean(liquidContext.language_code || document.documentElement.lang, 20),
      utm_source: clean(attribution.utm_source, 150),
      utm_medium: clean(attribution.utm_medium, 150),
      utm_campaign: clean(attribution.utm_campaign, 150),
    };
  };

  const flushPublishQueue = () => {
    publishTimer = 0;
    if (designMode || !window.Shopify?.analytics?.publish) return false;
    while (publishQueue.length) {
      const item = publishQueue.shift();
      try {
        window.Shopify.analytics.publish(`${EVENT_PREFIX}${item.name}`, item.data);
      } catch (_) {
        publishQueue.unshift(item);
        return false;
      }
    }
    return true;
  };

  const schedulePublish = () => {
    if (publishTimer || designMode) return;
    let attempts = 0;
    const retry = () => {
      publishTimer = 0;
      attempts += 1;
      if (flushPublishQueue() || attempts >= 20) return;
      publishTimer = window.setTimeout(retry, 250);
    };
    publishTimer = window.setTimeout(retry, 0);
  };

  const emit = (name, details = {}, onceKey = '') => {
    if (onceKey && emittedOnce.has(onceKey)) return;
    if (onceKey) emittedOnce.add(onceKey);
    const data = { ...commonPayload(), ...details };
    window.dispatchEvent(new CustomEvent('tan:lead-event', { detail: { name, data } }));
    if (designMode) return;
    publishQueue.push({ name, data });
    if (!flushPublishQueue()) schedulePublish();
  };

  const boundedNumber = (value, minimum, maximum, fallback = 0) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(maximum, Math.max(minimum, parsed));
  };
  const safeCurrency = (value) => {
    const currency = clean(value, 3).toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : 'EUR';
  };
  const designerRootFor = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    return target?.closest('[data-tan-neon-designer]') || null;
  };
  const designerContext = (root, stage, step) => ({
    funnel_name: 'neon_designer',
    intent_stage: stage,
    funnel_step: step,
    placement: root?.dataset.homeEmbed === 'true' ? 'home' : 'standalone',
    currency: safeCurrency(root?.dataset.currency),
  });

  const trackDesignerStart = (event) => {
    if (!event.isTrusted || event.isComposing === true) return;
    const input = event.target;
    if (!(input instanceof HTMLTextAreaElement) || !input.matches('[data-text-input]')) return;
    if (!input.value.trim()) return;
    const root = input.closest('[data-tan-neon-designer]');
    if (!root || designerStarts.has(root)) return;
    designerStarts.add(root);
    emit('designer_started', designerContext(root, 'start', 1));
  };

  const trackDesignerNext = (event) => {
    const root = designerRootFor(event);
    if (!root || designerNexts.has(root)) return;
    designerNexts.add(root);
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    emit('designer_next', {
      ...designerContext(root, 'next', 2),
      character_count: Math.round(boundedNumber(detail.character_count, 0, 500)),
      width_cm: Math.round(boundedNumber(detail.width_cm, 0, 1000)),
      height_cm: Math.round(boundedNumber(detail.height_cm, 0, 1000)),
      usage: clean(detail.usage, 60),
      backboard: clean(detail.backboard, 60),
      color_mode: clean(detail.color_mode, 40),
      icon_id: clean(detail.icon_id, 100),
    });
  };

  const trackDesignerAddToCart = (event) => {
    const root = designerRootFor(event);
    if (!root) return;
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    const value = boundedNumber(detail.value, 0, 1000000);
    if (value <= 0) return;
    emit('designer_added_to_cart', {
      ...designerContext(root, 'add_to_cart', 3),
      currency: safeCurrency(detail.currency || root.dataset.currency),
      value,
      quantity: Math.round(boundedNumber(detail.quantity, 1, 10, 1)),
      variant_id: clean(detail.variant_id, 80),
    });
  };

  const trackQuickQuoteEvent = (event) => {
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    const name = clean(detail.event, 60);
    if (!QUOTE_EVENT_NAMES.has(name)) return;
    emit(name, {
      funnel_name: 'custom_neon_quote',
      step_number: Math.round(boundedNumber(detail.step_number, 0, 20)),
      step_name: clean(detail.step_name, 100),
      market_country: clean(detail.market_country, 10).toUpperCase(),
      quote_currency: safeCurrency(detail.quote_currency),
      sign_purpose: clean(detail.sign_purpose, 100),
      quote_category: clean(detail.quote_category, 100),
      quote_type: clean(detail.quote_type, 100),
      quote_placement: clean(detail.quote_placement, 100),
      budget_tier: clean(detail.budget_tier, 100),
      budget_band: clean(detail.budget_band, 100),
      upload_slot: clean(detail.upload_slot, 20),
      has_file: Boolean(detail.has_file),
      file_count: Math.round(boundedNumber(detail.file_count, 0, 8)),
      restored_draft: Boolean(detail.restored_draft),
    });
  };

  const selectedChoices = (root) => {
    const selected = [];
    const grouped = Array.from(root.querySelectorAll('[data-choice-group].is-active, [data-choice-group][aria-pressed="true"]'));
    const seenGroups = new Set();
    grouped.forEach((choice) => {
      const group = clean(choice.dataset.choiceGroup, 30);
      if (!group || seenGroups.has(group)) return;
      seenGroups.add(group);
      const id = clean(choice.dataset.choiceId || choice.value || choice.textContent, 60);
      selected.push({ group, id, title: clean(choice.dataset.choiceTitle || choice.textContent, 100) });
    });
    const outdoor = root.querySelector('[data-outdoor-option].is-active, [data-outdoor-option][aria-pressed="true"]');
    if (outdoor) {
      selected.push({
        group: 'environment',
        id: clean(outdoor.dataset.outdoorOption, 60),
        title: clean(outdoor.querySelector('strong')?.textContent || outdoor.textContent, 100),
      });
    }
    return selected.slice(0, 3);
  };

  const briefOutput = (root) => {
    const explicit = root.querySelector('[data-tan-brief-output]');
    if (explicit?.value) return cleanMultiline(explicit.value);
    const choices = selectedChoices(root);
    const resultHeading = clean(root.querySelector('[data-tan-brief-result], [data-hospitality-result-heading], .tan-outdoor-fit__result:not([hidden]) h3')?.textContent, 240);
    const resultCopy = clean(root.querySelector('[data-hospitality-result-copy], .tan-outdoor-fit__result:not([hidden]) .tan-outdoor-fit__recommendation div')?.textContent, 800);
    const lines = [
      `${sourceHubLabel || currentHub?.label || 'Business'} project brief`,
      ...choices.map((choice) => `${choice.group}: ${choice.title}`),
      resultHeading ? `Planning direction: ${resultHeading}` : '',
      resultCopy,
    ].filter(Boolean);
    return cleanMultiline(lines.join('\n'));
  };

  const briefPayload = (root) => {
    const choices = selectedChoices(root);
    return {
      brief_attached: Boolean(briefOutput(root)),
      choice_1: choices[0] ? `${choices[0].group}:${choices[0].id}`.slice(0, 100) : '',
      choice_2: choices[1] ? `${choices[1].group}:${choices[1].id}`.slice(0, 100) : '',
      choice_3: choices[2] ? `${choices[2].group}:${choices[2].id}`.slice(0, 100) : '',
    };
  };

  const saveLeadContext = (root = null, sourceCta = '') => {
    const existing = currentLeadContext();
    const sameContentPage = Boolean(
      contentHub
      && existing.hub === contentHub.id
      && existing.source_path === window.location.pathname
    );
    const next = {
      ...existing,
      hub: contentHub?.id || sourceHubId || existing.hub || '',
      hub_label: contentHub?.label || sourceHubLabel || existing.hub_label || '',
      source_path: contentHub ? window.location.pathname : (existing.source_path || window.location.pathname),
      source_cta: sourceCta || (sameContentPage ? existing.source_cta : '') || '',
      landing_path: existing.landing_path || window.location.pathname,
      brief: root ? briefOutput(root) : (contentHub && !sameContentPage ? '' : (existing.brief || '')),
      brief_choices: root ? selectedChoices(root) : (contentHub && !sameContentPage ? [] : (existing.brief_choices || [])),
      quote_started_at: contentHub && !sameContentPage ? 0 : Number(existing.quote_started_at || 0),
      updated_at: Date.now(),
    };
    UTM_KEYS.forEach((key) => {
      const value = clean(query.get(key), 150);
      if (value) next[key] = value;
    });
    storage.set(STORAGE_KEY, next);
    return next;
  };

  const requiredGroups = (root) => {
    const groups = new Set(Array.from(root.querySelectorAll('[data-choice-group]')).map((choice) => choice.dataset.choiceGroup).filter(Boolean));
    if (root.querySelector('[data-outdoor-option]')) groups.add('environment');
    return groups;
  };

  const stateFor = (root) => {
    if (!briefStates.has(root)) {
      briefStates.set(root, { touched: new Set(), started: false, completed: false });
    }
    return briefStates.get(root);
  };

  const startBrief = (root) => {
    const state = stateFor(root);
    if (state.started) return;
    state.started = true;
    emit('brief_started', briefPayload(root), `brief_started:${templateSuffix}`);
  };

  const completeBrief = (root) => {
    const state = stateFor(root);
    if (state.completed) return;
    state.completed = true;
    saveLeadContext(root);
    emit('brief_completed', briefPayload(root), `brief_completed:${templateSuffix}`);
  };

  const inferCta = (anchor) => {
    if (anchor.dataset.tanCta) return clean(anchor.dataset.tanCta, 100);
    const containers = [
      ['[data-tan-brief]', 'brief'],
      ['.tan-hero', 'hero'],
      ['.tan-hospitality-routes', 'project_route'],
      ['.tan-outdoor-spec', 'outdoor_spec'],
      ['.tan-hospitality-set', 'brand_set'],
      ['.tan-b2b-cta', 'final_cta'],
      ['header', 'header'],
    ];
    for (const [selector, label] of containers) {
      const container = anchor.closest(selector);
      if (!container) continue;
      const links = Array.from(container.querySelectorAll('a[href]')).filter((link) => !link.hidden);
      return `${label}_${Math.max(1, links.indexOf(anchor) + 1)}`;
    }
    return 'quote_link';
  };

  const isQuoteLink = (anchor) => {
    try {
      const pathname = new URL(anchor.href, window.location.href).pathname;
      return QUOTE_PATHS.some((path) => pathname.endsWith(path));
    } catch (_) {
      return false;
    }
  };

  const decorateQuoteLink = (anchor, leadContext, cta) => {
    try {
      const url = new URL(anchor.href, window.location.href);
      if (leadContext.hub) url.searchParams.set('tan_hub', leadContext.hub);
      if (cta) url.searchParams.set('tan_cta', cta);
      if (leadContext.brief) url.searchParams.set('tan_brief', '1');
      anchor.href = url.toString();
    } catch (_) {
      // Navigation remains unchanged for malformed or unsupported links.
    }
  };

  const leadFieldValues = (leadContext) => ({
    '[data-tan-lead-hub]': localizedHubLabel(leadContext.hub) || leadContext.hub_label || leadContext.hub || '',
    '[data-tan-lead-source-path]': leadContext.source_path || window.location.pathname || 'Direct',
    '[data-tan-lead-source-cta]': query.get('tan_cta') || leadContext.source_cta || 'Direct',
    '[data-tan-lead-brief]': leadContext.brief || '',
    '[data-tan-lead-utm-source]': leadContext.utm_source || '',
    '[data-tan-lead-utm-medium]': leadContext.utm_medium || '',
    '[data-tan-lead-utm-campaign]': leadContext.utm_campaign || '',
    '[data-tan-lead-utm-content]': leadContext.utm_content || '',
    '[data-tan-lead-utm-term]': leadContext.utm_term || '',
    '[data-tan-lead-first-utm-source]': leadContext.first_utm_source || '',
    '[data-tan-lead-first-utm-medium]': leadContext.first_utm_medium || '',
    '[data-tan-lead-first-utm-campaign]': leadContext.first_utm_campaign || '',
    '[data-tan-lead-first-utm-content]': leadContext.first_utm_content || '',
    '[data-tan-lead-first-utm-term]': leadContext.first_utm_term || '',
    '[data-tan-lead-landing-path]': leadContext.landing_path || '',
    '[data-tan-lead-referrer]': leadContext.landing_referrer || '',
    '[data-tan-lead-gclid]': leadContext.gclid || '',
    '[data-tan-lead-gbraid]': leadContext.gbraid || '',
    '[data-tan-lead-wbraid]': leadContext.wbraid || '',
    '[data-tan-lead-dclid]': leadContext.dclid || '',
    '[data-tan-lead-fbclid]': leadContext.fbclid || '',
    '[data-tan-lead-msclkid]': leadContext.msclkid || '',
    '[data-tan-lead-ttclid]': leadContext.ttclid || '',
  });

  const fillFormContext = () => {
    const formRoot = document.querySelector('[data-tan-configurator]');
    if (!formRoot) return;
    const form = formRoot.querySelector('form');
    if (!form) return;
    const leadContext = currentLeadContext();
    const values = leadFieldValues(leadContext);
    Object.entries(values).forEach(([selector, value]) => {
      const field = form.querySelector(selector);
      if (!field) return;
      const normalized = String(value).slice(0, selector.includes('brief') ? 6000 : 300);
      field.defaultValue = normalized;
      field.value = normalized;
    });

    const attached = formRoot.querySelector('[data-tan-brief-attached]');
    const attachedText = attached?.querySelector('[data-tan-brief-attached-text]');
    if (attached) attached.hidden = !leadContext.brief;
    if (attachedText && leadContext.brief) {
      const hubLabel = localizedHubLabel(leadContext.hub)
        || leadContext.hub_label
        || clean(leadI18n.brief?.your_sector, 100);
      attachedText.textContent = String(leadI18n.brief?.attached_template || '').replace('__TAN_HUB__', hubLabel);
    }

    const rightsGate = formRoot.querySelector('[data-tan-automotive-rights]');
    const rightsInput = rightsGate?.querySelector('[data-tan-automotive-rights-input]');
    const needsAutomotiveRights = (leadContext.hub || sourceHubId) === 'automotive';
    if (rightsGate) rightsGate.hidden = !needsAutomotiveRights;
    if (rightsInput) {
      rightsInput.disabled = !needsAutomotiveRights;
      rightsInput.required = needsAutomotiveRights;
    }

    if (formRoot.matches('[data-tan-quick-quote]') || formRoot.closest('[data-tan-quick-quote]')) return;
    if (form.dataset.tanLeadReady === 'true') return;
    form.dataset.tanLeadReady = 'true';

    const quoteStarted = () => {
      const latest = currentLeadContext();
      if (Date.now() - Number(latest.quote_started_at || 0) < 30 * 60 * 1000) return;
      latest.quote_started_at = Date.now();
      storage.set(STORAGE_KEY, latest);
      emit('quote_started', {
        brief_attached: Boolean(latest.brief),
      }, `quote_started:${latest.quote_started_at}`);
    };

    form.addEventListener('input', quoteStarted, { once: true });
    form.addEventListener('change', quoteStarted, { once: true });
    formRoot.querySelector('[data-logo-input]')?.addEventListener('click', () => {
      quoteStarted();
      emit('upload_logo_clicked', { brief_attached: Boolean(leadContext.brief) }, `upload_logo_clicked:${leadContext.updated_at || window.location.pathname}`);
    });

    form.addEventListener('submit', () => {
      const quantity = Number(form.querySelector('[name="contact[Quantity]"]')?.value || 1);
      const pending = {
        submitted_at: Date.now(),
        hub: leadContext.hub || sourceHubId || 'direct',
        hub_label: leadContext.hub_label || sourceHubLabel || '',
        source_path: leadContext.source_path || window.location.pathname,
        source_cta: leadContext.source_cta || query.get('tan_cta') || 'direct',
        brief_attached: Boolean(leadContext.brief),
        design_type: clean(form.querySelector('[name="contact[Design type]"]:checked')?.value, 100),
        size_band: clean(form.querySelector('[name="contact[Approximate size]"]')?.value, 100),
        budget_band: clean(form.querySelector('[name="contact[Estimated budget]"]')?.value, 100),
        purpose: clean(form.querySelector('[name="contact[Sign purpose]"]')?.value, 100),
        environment: clean(form.querySelector('[name="contact[Installation environment]"]')?.value, 100),
        delivery_timing: clean(form.querySelector('[name="contact[Preferred delivery time]"]')?.value, 100),
        quantity_band: quantity <= 1 ? '1' : quantity <= 5 ? '2_5' : quantity <= 10 ? '6_10' : '11_plus',
        has_artwork_link: Boolean(form.querySelector('[name="contact[Artwork share link]"]')?.value),
      };
      storage.set(PENDING_KEY, pending);
    });

    const success = formRoot.querySelector('.tan-configurator__notice--success');
    const pending = storage.get(PENDING_KEY);
    const reported = storage.get(REPORTED_KEY);
    if (success && pending?.submitted_at && pending.submitted_at !== reported?.submitted_at) {
      emit('quote_submitted', {
        brief_attached: Boolean(pending.brief_attached),
        design_type: pending.design_type,
        size_band: pending.size_band,
        budget_band: pending.budget_band,
        purpose: pending.purpose,
        environment: pending.environment,
        delivery_timing: pending.delivery_timing,
        quantity_band: pending.quantity_band,
        has_artwork_link: Boolean(pending.has_artwork_link),
      }, `quote_submitted:${pending.submitted_at}`);
      storage.set(REPORTED_KEY, { submitted_at: pending.submitted_at });
    }
  };

  let formRefillTimer = 0;
  const refillFormContext = () => {
    fillFormContext();
    window.clearTimeout(formRefillTimer);
    formRefillTimer = window.setTimeout(fillFormContext, 250);
  };

  const applyFormDataContext = (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form?.closest('[data-tan-configurator]') || !event.formData) return;
    const leadContext = currentLeadContext();
    Object.entries(leadFieldValues(leadContext)).forEach(([selector, value]) => {
      const field = form.querySelector(selector);
      if (!field?.name) return;
      const normalized = String(value).slice(0, selector.includes('brief') ? 6000 : 300);
      event.formData.set(field.name, normalized);
    });
  };

  const initializeBriefs = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-tan-brief]')) roots.push(scope);
    scope.querySelectorAll?.('[data-tan-brief]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      stateFor(root);
      saveLeadContext(root);
    });
  };

  document.addEventListener('input', trackDesignerStart);
  document.addEventListener('compositionend', trackDesignerStart);
  document.addEventListener('tan:designer:review', trackDesignerNext);
  document.addEventListener('tan:designer:added-to-cart', trackDesignerAddToCart);
  document.addEventListener('tan:quote:event', trackQuickQuoteEvent);
  document.addEventListener('tan:quote:ready', refillFormContext);
  document.addEventListener('tan:quote:reset', refillFormContext);
  document.addEventListener('reset', () => window.setTimeout(refillFormContext, 0), true);
  document.addEventListener('submit', fillFormContext, true);
  document.addEventListener('formdata', applyFormDataContext, true);
  document.addEventListener('visitorConsentCollected', () => {
    storedContext = { ...storedContext, ...captureAttribution() };
    refillFormContext();
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const root = target.closest('[data-tan-brief]');
    const choice = target.closest('[data-choice-group], [data-outdoor-option]');
    if (root && choice) {
      const state = stateFor(root);
      const group = choice.dataset.choiceGroup || 'environment';
      state.touched.add(group);
      startBrief(root);
      window.setTimeout(() => {
        saveLeadContext(root);
        const groups = requiredGroups(root);
        if (groups.size && Array.from(groups).every((name) => state.touched.has(name))) completeBrief(root);
      }, 0);
    }

    const copy = target.closest('[data-tan-brief-copy]');
    if (root && copy) {
      startBrief(root);
      window.setTimeout(() => {
        completeBrief(root);
        emit('brief_copied', briefPayload(root));
      }, 0);
    }

    const anchor = target.closest('a[href]');
    if (!anchor || !isQuoteLink(anchor)) return;
    const cta = inferCta(anchor);
    if (root) {
      startBrief(root);
      completeBrief(root);
    }
    const leadContext = saveLeadContext(root, cta);
    leadContext.quote_started_at = Date.now();
    storage.set(STORAGE_KEY, leadContext);
    decorateQuoteLink(anchor, leadContext, cta);
    emit('quote_started', {
      source_cta: cta,
      brief_attached: Boolean(leadContext.brief),
      ...(root ? briefPayload(root) : {}),
    }, `quote_started:${leadContext.quote_started_at}`);
  });

  document.addEventListener('input', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const root = target?.closest('[data-tan-brief]');
    if (!root) return;
    startBrief(root);
    window.setTimeout(() => saveLeadContext(root), 0);
  });

  if (contentHub) {
    saveLeadContext();
    emit('hub_viewed', {}, `hub_viewed:${templateSuffix}:${window.location.pathname}`);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeBriefs();
      refillFormContext();
    }, { once: true });
  } else {
    initializeBriefs();
    refillFormContext();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeBriefs(event.target);
    refillFormContext();
  });

  window.addEventListener('load', refillFormContext, { once: true });
  window.addEventListener('pageshow', refillFormContext);

  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) return;
        storedContext = { ...storedContext, ...captureAttribution() };
        refillFormContext();
      }
    );
  }
})();
